import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * ErrorBoundary — catches unhandled React rendering errors.
 *
 * Prevents the entire app from going white-screen when a single
 * component tree throws during render. Shows a branded fallback UI
 * with retry and home navigation options.
 *
 * SRE R3: Single-component crashes no longer cause full-page blank screens.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log to console for developer debugging
    console.error('[ErrorBoundary] Caught render error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo?.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className="min-h-screen flex items-center justify-center"
          style={{ background: '#e5e4df' }}
        >
          <div
            className="bg-[#f8f7f4] rounded-lg shadow-xl p-8 text-center max-w-[480px] mx-4"
            style={{ border: '1px solid rgba(201,168,76,0.2)' }}
          >
            {/* Icon */}
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: 'rgba(220,38,38,0.1)' }}
            >
              <AlertTriangle className="w-7 h-7 text-[#dc2626]" />
            </div>

            {/* Title */}
            <h2
              className="text-lg font-semibold text-[#1a1a2e] mb-2"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Something went wrong
            </h2>

            {/* Message */}
            <p className="text-sm text-[#6b7280] mb-2">
              An unexpected error occurred while rendering this page.
              This has been logged and can be fixed.
            </p>

            {/* Error details (collapsible, for devs) */}
            {this.state.error && (
              <details className="text-left mb-4">
                <summary className="text-xs text-[#9ca3af] cursor-pointer hover:text-[#6b7280]">
                  Technical details
                </summary>
                <pre
                  className="mt-2 p-3 rounded text-xs overflow-auto max-h-[150px]"
                  style={{
                    background: '#1a1a2e',
                    color: '#f87171',
                    fontFamily: 'monospace',
                    fontSize: '11px',
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack
                    ? '\n\nComponent Stack:\n' + this.state.errorInfo.componentStack
                    : ''}
                </pre>
              </details>
            )}

            {/* Actions */}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                style={{
                  background: '#c9a84c',
                  color: '#1a1a2e',
                }}
              >
                <RefreshCw className="w-4 h-4" />
                Retry
              </button>
              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                style={{
                  background: 'transparent',
                  color: '#6b7280',
                  border: '1px solid rgba(107,114,128,0.3)',
                }}
              >
                <Home className="w-4 h-4" />
                Go to Dashboard
              </button>
            </div>

            {/* Brand footer */}
            <p className="text-xs text-[#c9a84c] mt-5 opacity-60">
              Revolead Construction
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
